import { z } from 'zod';

import { decimalSchema } from './common';

export const equipmentStatuses = ['available', 'maintenance', 'decommissioned'] as const;
export const fuelTypes = ['diesel', 'petrol', 'gas', 'electric', 'none'] as const;
export const rentalStatuses = ['open', 'returned', 'cancelled'] as const;
/** Fixed litres per engine hour, or litres per 100 km of mileage. */
export const fuelConsumptionModes = ['per_hour', 'per_100km'] as const;
export type FuelConsumptionMode = (typeof fuelConsumptionModes)[number];

/** Russian plates and machinery numbers: letters, digits, spaces, dashes. */
export const registrationNumberSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^([0-9A-ZА-ЯЁ][0-9A-ZА-ЯЁ -]{0,19})?$/, 'Госномер: до 20 букв, цифр, пробелов и дефисов');

export const equipmentSchema = z.object({
  id: z.string(),
  inventoryNumber: z.string(),
  name: z.string(),
  catalogItemId: z.string().nullable(),
  purchasePrice: z.string(),
  residualValue: z.string(),
  usefulLifeMonths: z.number().int(),
  fuelType: z.enum(fuelTypes),
  fuelConsumptionPerHour: z.string(),
  dailyOverhead: z.string(),
  currency: z.string(),
  status: z.enum(equipmentStatuses),
  version: z.number().int(),
  registrationNumber: z.string(),
  vehicleTypeId: z.number().int().nullable(),
  vehicleTypeName: z.string(),
  fuelConsumptionMode: z.enum(fuelConsumptionModes),
  fuelConsumptionPer100Km: z.string(),
  /** FortMonitor object the machine is tracked by. */
  telematicsObjectId: z.number().int().nullable(),
});
export type Equipment = z.infer<typeof equipmentSchema>;

export const createEquipmentSchema = z.object({
  inventoryNumber: z.string().trim().min(1, 'Введите инвентарный номер').max(64),
  name: z.string().trim().min(1, 'Введите название').max(200),
  purchasePrice: decimalSchema,
  residualValue: decimalSchema.default('0'),
  usefulLifeMonths: z.coerce.number().int().min(1, 'Минимум 1 месяц').max(1200),
  fuelType: z.enum(fuelTypes),
  fuelConsumptionPerHour: decimalSchema.default('0'),
  dailyOverhead: decimalSchema.default('0'),
  registrationNumber: registrationNumberSchema.default(''),
  vehicleTypeId: z.number().int().positive().nullable().default(null),
  fuelConsumptionMode: z.enum(fuelConsumptionModes).default('per_hour'),
  fuelConsumptionPer100Km: decimalSchema.default('0'),
  telematicsObjectId: z.number().int().positive().nullable().default(null),
});
export type CreateEquipment = z.infer<typeof createEquipmentSchema>;

/** Only the given fields change; null removes the vehicle type or the tracker link. */
export const updateEquipmentSchema = z.object({
  status: z.enum(equipmentStatuses).optional(),
  name: z.string().trim().min(1, 'Введите название').max(200).optional(),
  registrationNumber: registrationNumberSchema.optional(),
  vehicleTypeId: z.number().int().positive().nullable().optional(),
  fuelConsumptionMode: z.enum(fuelConsumptionModes).optional(),
  fuelConsumptionPerHour: decimalSchema.optional(),
  fuelConsumptionPer100Km: decimalSchema.optional(),
  telematicsObjectId: z.number().int().positive().nullable().optional(),
  expectedVersion: z.number().int(),
});
export type UpdateEquipment = z.infer<typeof updateEquipmentSchema>;

export const rentalSchema = z.object({
  id: z.string(),
  equipmentId: z.string(),
  workerUserId: z.number().int(),
  startsAt: z.string(),
  endsAt: z.string(),
  returnedAt: z.string().nullable(),
  status: z.enum(rentalStatuses),
  shiftHoursPerDay: z.string(),
  purpose: z.string(),
  externalStatus: z.enum(['pending', 'submitted', 'failed']),
  /** Km on issue and on return, for equipment counted per 100 km without a tracker. */
  odometerStart: z.string().nullable(),
  odometerEnd: z.string().nullable(),
});
export type Rental = z.infer<typeof rentalSchema>;

export const createRentalSchema = z
  .object({
    equipmentId: z.string().min(1, 'Выберите технику'),
    startsAt: z.iso.datetime({ offset: true, message: 'Укажите дату и время начала' }),
    endsAt: z.iso.datetime({ offset: true, message: 'Укажите дату и время окончания' }),
    workerUserId: z.number().int().positive().optional(),
    shiftHoursPerDay: decimalSchema.optional(),
    purpose: z.string().trim().max(1000).default(''),
    odometerStart: decimalSchema.optional(),
  })
  .refine((rental) => Date.parse(rental.endsAt) > Date.parse(rental.startsAt), {
    message: 'Окончание должно быть позже начала',
    path: ['endsAt'],
  });
export type CreateRental = z.infer<typeof createRentalSchema>;

/** Returning equipment counted per 100 km without a tracker needs the odometer. */
export const returnRentalSchema = z.object({ odometerEnd: decimalSchema.optional() });
export type ReturnRental = z.infer<typeof returnRentalSchema>;

export const rentalFilterSchema = z.object({
  status: z.enum(rentalStatuses).optional(),
  equipmentId: z.string().optional(),
  workerUserId: z.coerce.number().int().positive().optional(),
});
export type RentalFilter = z.infer<typeof rentalFilterSchema>;

export const costLineSchema = z.object({
  costType: z.string(),
  amount: z.string(),
  quantity: z.string().nullable(),
  unit: z.string().nullable(),
  description: z.string(),
});

export const rentalCostsSchema = z.object({
  rentalId: z.string(),
  currency: z.string(),
  total: z.string(),
  complete: z.boolean(),
  totalsByType: z.array(z.object({ costType: z.string(), amount: z.string() })),
  days: z.array(
    z.object({
      date: z.string(),
      periodStart: z.string(),
      periodEnd: z.string(),
      total: z.string(),
      lines: z.array(costLineSchema),
    }),
  ),
});
export type RentalCosts = z.infer<typeof rentalCostsSchema>;

export const vehicleTypeSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  /** "Спецтехника / Экскаваторы": the place in the ReviewPlatform catalog. */
  path: z.string(),
});
export type VehicleType = z.infer<typeof vehicleTypeSchema>;
